import { useState } from "react";
import { apiFetch } from "@/lib/api";
import { apiUrl } from "@/lib/api-base";

const NAVY = "#0B308E";
const GOLD = "#C9A84C";
const DEV_TOKEN = "igc-admin-dev-token-change-in-production";

/* ─── Types ─────────────────────────────────── */
interface JoinRequest {
  id: number;
  surname: string;
  name: string;
  email: string;
  country: string;
  speciality: string;
  cvFilename?: string;
  createdAt: string;
}

interface ContactRequest {
  id: number;
  name: string;
  email: string;
  organisation?: string;
  country?: string;
  message: string;
  createdAt: string;
}

/* ─── Helpers ───────────────────────────────── */
function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const th = "px-4 py-3 text-xs font-semibold text-left tracking-wide uppercase bg-gray-50 border-b border-gray-200";
const td = "px-4 py-3 text-sm border-b border-gray-100 align-top";

/* ─── Main Component ────────────────────────── */
export default function Admin() {
  const [tokenInput, setTokenInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [activeToken, setActiveToken] = useState("");
  const [activeTab, setActiveTab] = useState<"join" | "contact">("join");
  const [joinData, setJoinData] = useState<JoinRequest[]>([]);
  const [contactData, setContactData] = useState<ContactRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchData = async (tok: string) => {
    setLoading(true);
    setError("");
    try {
      const [jr, cr] = await Promise.all([
        apiFetch(`/admin/join-requests?token=${encodeURIComponent(tok)}`, { auth: false }).then((r) => r.json()),
        apiFetch(`/admin/contact-requests?token=${encodeURIComponent(tok)}`, { auth: false }).then((r) => r.json()),
      ]);
      if (jr.success) {
        setJoinData(jr.data.requests as JoinRequest[]);
      } else {
        setError((jr.error?.message as string) || "Unauthorized or error loading join requests.");
      }
      if (cr.success) {
        setContactData(cr.data.requests as ContactRequest[]);
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    if (!tokenInput.trim()) return;
    setActiveToken(tokenInput.trim());
    setAuthed(true);
    fetchData(tokenInput.trim());
  };

  /* ── Login Screen ─────────────────────────── */
  if (!authed) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", boxShadow: "0 4px 32px rgba(0,0,0,0.08)", padding: "48px 40px", width: "100%", maxWidth: 420 }}>
          {/* Logo area */}
          <div className="flex justify-center mb-6">
            <div style={{ backgroundColor: NAVY, display: "inline-flex", alignItems: "center", padding: "8px 14px" }}>
              <span style={{ color: "#fff", fontFamily: '"Times New Roman", Times, serif', fontSize: "1.6rem", fontWeight: "normal", lineHeight: 1, letterSpacing: "0.02em" }}>I</span>
              <span style={{ width: "1px", backgroundColor: "#fff", height: "1.3rem", margin: "0 9px", display: "block" }} />
              <span style={{ color: "#fff", fontFamily: '"Times New Roman", Times, serif', fontSize: "1.6rem", fontWeight: "normal", lineHeight: 1, letterSpacing: "0.02em" }}>G</span>
              <span style={{ width: "1px", backgroundColor: "#fff", height: "1.3rem", margin: "0 9px", display: "block" }} />
              <span style={{ color: "#fff", fontFamily: '"Times New Roman", Times, serif', fontSize: "1.6rem", fontWeight: "normal", lineHeight: 1, letterSpacing: "0.02em" }}>C</span>
            </div>
          </div>
          <h1 style={{ color: NAVY, fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "1.6rem", fontWeight: 600, textAlign: "center", marginBottom: 8 }}>
            Admin Dashboard
          </h1>
          <p style={{ color: "#888", textAlign: "center", fontSize: "0.85rem", marginBottom: 28 }}>
            Enter your admin token to continue
          </p>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: NAVY, marginBottom: 6, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Admin Token
            </label>
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && login()}
              placeholder="Enter admin token"
              style={{
                width: "100%", padding: "10px 14px", fontSize: "0.875rem",
                border: "1px solid #d1d5db", outline: "none", background: "#fff",
                boxSizing: "border-box",
              }}
            />
          </div>

          <button
            onClick={login}
            style={{
              width: "100%", padding: "10px 0", fontSize: "0.875rem",
              fontWeight: 600, background: NAVY, color: "#fff",
              border: "none", cursor: "pointer", letterSpacing: "0.05em",
            }}
          >
            Sign In
          </button>

          <p style={{ color: "#aaa", fontSize: "0.75rem", textAlign: "center", marginTop: 20 }}>
            Development token: <code style={{ fontFamily: "monospace", fontSize: "0.7rem" }}>{DEV_TOKEN}</code>
          </p>
        </div>
      </div>
    );
  }

  /* ── Dashboard ────────────────────────────── */
  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Top bar */}
      <div style={{ background: NAVY, padding: "12px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div className="flex items-center gap-4">
          <div style={{ backgroundColor: "#fff", display: "inline-flex", alignItems: "center", padding: "5px 10px" }}>
            <span style={{ color: NAVY, fontFamily: '"Times New Roman", Times, serif', fontSize: "1.2rem", fontWeight: "normal" }}>I</span>
            <span style={{ width: "1px", backgroundColor: NAVY, height: "1rem", margin: "0 7px" }} />
            <span style={{ color: NAVY, fontFamily: '"Times New Roman", Times, serif', fontSize: "1.2rem", fontWeight: "normal" }}>G</span>
            <span style={{ width: "1px", backgroundColor: NAVY, height: "1rem", margin: "0 7px" }} />
            <span style={{ color: NAVY, fontFamily: '"Times New Roman", Times, serif', fontSize: "1.2rem", fontWeight: "normal" }}>C</span>
          </div>
          <span style={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}>Admin Dashboard</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchData(activeToken)}
            disabled={loading}
            style={{
              padding: "6px 16px", fontSize: "0.8rem", fontWeight: 600,
              background: loading ? "#8a9fc5" : GOLD, color: "#fff",
              border: "none", cursor: loading ? "not-allowed" : "pointer",
              letterSpacing: "0.05em",
            }}
          >
            {loading ? "Loading…" : "↻ Refresh"}
          </button>
          <button
            onClick={() => { setAuthed(false); setTokenInput(""); setJoinData([]); setContactData([]); }}
            style={{
              padding: "6px 16px", fontSize: "0.8rem", fontWeight: 500,
              background: "rgba(255,255,255,0.15)", color: "#fff",
              border: "1px solid rgba(255,255,255,0.3)", cursor: "pointer",
            }}
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Gold stripe */}
      <div style={{ height: 3, background: `linear-gradient(90deg, ${NAVY}, ${GOLD})` }} />

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px" }}>
        {/* Error banner */}
        {error && (
          <div style={{ background: "#fff0f0", border: "1px solid #fca5a5", padding: "12px 16px", color: "#b91c1c", fontSize: "0.875rem", marginBottom: 24, borderRadius: 2 }}>
            {error}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6" style={{ maxWidth: 400 }}>
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", padding: "16px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ fontSize: "1.8rem", fontWeight: 700, color: NAVY }}>{joinData.length}</div>
            <div style={{ fontSize: "0.75rem", color: "#888", fontWeight: 500, marginTop: 2 }}>JOIN APPLICATIONS</div>
          </div>
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", padding: "16px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ fontSize: "1.8rem", fontWeight: 700, color: NAVY }}>{contactData.length}</div>
            <div style={{ fontSize: "0.75rem", color: "#888", fontWeight: 500, marginTop: 2 }}>CONTACT REQUESTS</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, borderBottom: "2px solid #e5e7eb", marginBottom: 24 }}>
          {(["join", "contact"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: "10px 28px",
                fontSize: "0.875rem",
                fontWeight: 600,
                background: "transparent",
                border: "none",
                borderBottom: activeTab === tab ? `3px solid ${GOLD}` : "3px solid transparent",
                color: activeTab === tab ? NAVY : "#888",
                cursor: "pointer",
                marginBottom: -2,
                transition: "color 0.2s",
                letterSpacing: "0.03em",
              }}
            >
              {tab === "join" ? `Join Applications (${joinData.length})` : `Contact Requests (${contactData.length})`}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#aaa" }}>
            <div style={{ fontSize: "2rem", marginBottom: 12 }}>⏳</div>
            <p>Loading data…</p>
          </div>
        ) : activeTab === "join" ? (
          /* ── Join Applications ─── */
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", overflowX: "auto" }}>
            {joinData.length === 0 ? (
              <div style={{ textAlign: "center", padding: "64px 24px", color: "#aaa" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>📭</div>
                <p>No join applications yet.</p>
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
                <thead>
                  <tr>
                    <th className={th} style={{ color: NAVY, width: 40 }}>#</th>
                    <th className={th} style={{ color: NAVY }}>Surname</th>
                    <th className={th} style={{ color: NAVY }}>Name</th>
                    <th className={th} style={{ color: NAVY }}>Email</th>
                    <th className={th} style={{ color: NAVY }}>Country</th>
                    <th className={th} style={{ color: NAVY }}>Speciality</th>
                    <th className={th} style={{ color: NAVY }}>CV</th>
                    <th className={th} style={{ color: NAVY }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {joinData.map((row, idx) => (
                    <tr key={row.id} style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                      <td className={td} style={{ color: "#aaa" }}>{idx + 1}</td>
                      <td className={td} style={{ fontWeight: 500 }}>{row.surname}</td>
                      <td className={td}>{row.name}</td>
                      <td className={td}>
                        <a href={`mailto:${row.email}`} style={{ color: NAVY, textDecoration: "none" }}>{row.email}</a>
                      </td>
                      <td className={td}>{row.country || "—"}</td>
                      <td className={td}>
                        <span style={{ background: `${GOLD}22`, color: NAVY, padding: "2px 8px", fontSize: "0.75rem", fontWeight: 500, borderRadius: 2 }}>
                          {row.speciality || "—"}
                        </span>
                      </td>
                      <td className={td}>
                        {row.cvFilename ? (
                          <a
                            href={apiUrl(`/admin/cv/${row.id}?token=${encodeURIComponent(activeToken)}`)}
                            download={row.cvFilename}
                            style={{ color: NAVY, textDecoration: "none", display: "flex", alignItems: "center", gap: 4, fontSize: "0.8rem", fontWeight: 500 }}
                          >
                            📎 {row.cvFilename}
                          </a>
                        ) : (
                          <span style={{ color: "#ccc" }}>—</span>
                        )}
                      </td>
                      <td className={td} style={{ color: "#888", whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                        {fmtDate(row.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          /* ── Contact Requests ─── */
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", overflowX: "auto" }}>
            {contactData.length === 0 ? (
              <div style={{ textAlign: "center", padding: "64px 24px", color: "#aaa" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>📭</div>
                <p>No contact requests yet.</p>
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                <thead>
                  <tr>
                    <th className={th} style={{ color: NAVY, width: 40 }}>#</th>
                    <th className={th} style={{ color: NAVY }}>Name</th>
                    <th className={th} style={{ color: NAVY }}>Email</th>
                    <th className={th} style={{ color: NAVY }}>Organisation</th>
                    <th className={th} style={{ color: NAVY }}>Country</th>
                    <th className={th} style={{ color: NAVY }}>Message</th>
                    <th className={th} style={{ color: NAVY }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {contactData.map((row, idx) => (
                    <tr key={row.id} style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                      <td className={td} style={{ color: "#aaa" }}>{idx + 1}</td>
                      <td className={td} style={{ fontWeight: 500 }}>{row.name}</td>
                      <td className={td}>
                        <a href={`mailto:${row.email}`} style={{ color: NAVY, textDecoration: "none" }}>{row.email}</a>
                      </td>
                      <td className={td}>{row.organisation || "—"}</td>
                      <td className={td}>{row.country || "—"}</td>
                      <td className={td} style={{ maxWidth: 280 }}>
                        <p style={{ margin: 0, lineHeight: 1.5, color: "#555", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}>
                          {row.message}
                        </p>
                      </td>
                      <td className={td} style={{ color: "#888", whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                        {fmtDate(row.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
